// signalCatalog.js — the dashboard's signal registry, sourced from the backend.
//
// The LIST of signals, their namespaces, labels, descriptions, and per-tenant
// deployment state now come from `ListSignals` (the manifest is the backend
// source of truth). The FE only DECORATES: a thin `namespace → { command, tier }`
// overlay supplies the stable URL/Library slug (`command`) and the console tier
// (alert vs audit) — neither of which the backend models. A backend detection
// signal with no overlay entry still renders: its command is derived from the
// namespace and its tier defaults to `alert` (this is how a BE-only signal such
// as `package_install` appears with no FE edit).
//
// Sourcing is async (an RPC), but the query hot path (apiClient / analyticsClient
// / signalsDim) reads this synchronously. Bridge: a hydrate-once module cache.
// `hydrateSignalCatalog()` fetches once (kicked at app bootstrap, and lazily by
// `useSignalCatalog`); the sync accessors read the cache and return empty until
// it lands. Signals surfaces gate on readiness via the hook; other callers
// self-heal once hydration completes.
//
// Cache shape — a DELIBERATE third mode, distinct from userInfo.js /
// permissions.js. Those cache one immutable posture with `useState(_cache) +
// useEffect([])` (fire-once: an already-mounted component never observes a later
// cache change — fine, `/v1/me` doesn't change mid-session). This catalog MUST
// re-render mounted components on a LATER change: the empty→populated
// rollout-skew self-heal and the visibility-revalidate both mutate the cache
// after mount, so subscribers need to see it. Hence `useSyncExternalStore` over a
// listener set, not the fire-once `useState`. It keeps the same `_inflight`
// single-flight + `pick`-style dual-shape reads (in signalsApi.js) as the siblings.
//
// The LISTING RULE (the Signals tab): a signal is listed iff it is a DETECTION
// that is ENABLED for the caller's tenant (`deployed_enabled`). Extraction
// signals never list here — they feed their own lenses by namespace. So the
// listing legitimately varies per tenant and is a deploy-state fact, not a bug.
import { useEffect, useSyncExternalStore } from "react";
import { listSignals } from "./signalsApi.js";
import {
    DETECTION_OVERLAY,
    detectionCommandForNamespace,
} from "./signalIdentity.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

// Overlay of ONLY the detection namespaces whose `command` (URL/Library slug),
// `tier` (console presentation), or `subject` DIVERGES from the derived default —
// `deriveCommand(namespace)`, tier `alert`, subject `prompt`. Every other
// detection is derived, so this holds just the exceptions, not a copy of the
// whole set:
//   • two commands diverge from the `security.<stem>` derivation;
//   • three are `audit`-tier (normal agent activity, ranked below alerts);
//   • two are endpoint-subject (see `subject` below).
// Each entry overrides only the field that differs (see `_build`). A detection
// absent here — including a BE-only one like `security.package_install` — is
// fully derived, no FE edit.
//
// `subject` is which entity the signal's tags are ABOUT, and it selects the
// detail presentation: `prompt` lists the prompts that fired (SignalDetailBody),
// `endpoint` lists the endpoints that fired (EndpointSignalDetailBody). It lives
// here rather than on the wire because nothing in the signal registry declares
// it: subject is a property of the emitted tag (`signal_tags.subject_type`), not
// of the rule, and the lens layer needs the answer synchronously — before any tag
// has been read — to pick a body. The second endpoint-subject detection
// (guardrail_disable) has now landed; promoting subject to the manifest +
// ListSignals proto is due as its own change.
// Default when a detection declares no `subject` overlay: its tags annotate the
// prompt/session that produced them.
const DEFAULT_SUBJECT = "prompt";

// command → subject, derived from the overlay at MODULE LOAD, deliberately not
// from the hydrated catalog. Lens resolution runs `when(step)` synchronously and
// can fire before (or without) a `listSignals` round-trip — a deeplink straight
// into an endpoint-subject signal must resolve the right body on first render,
// not flip to it once the catalog arrives.
const SUBJECT_BY_COMMAND = new Map(
    Object.entries(DETECTION_OVERLAY)
        .filter(([, o]) => o.subject)
        .map(([ns, o]) => [detectionCommandForNamespace(ns), o.subject]),
);

// Which entity a detection's tags are about — "prompt" (default) or "endpoint".
// Synchronous and hydration-independent; see SUBJECT_BY_COMMAND.
export function subjectForCommand(command) {
    if (!command) return DEFAULT_SUBJECT;
    return SUBJECT_BY_COMMAND.get(String(command)) || DEFAULT_SUBJECT;
}

function emptyState(status = "idle", error = null) {
    return { status, error, byCommand: new Map(), byNamespace: new Map(), detections: [], schemaServed: false };
}

let _state = emptyState();
const _listeners = new Set();
let _inflight = null;

function _snapshot() { return _state; }

function _emit() {
    for (const l of _listeners) l(_state);
}

onAuthIdentityChange(() => {
    _state = emptyState();
    _inflight = null;
    _emit();
});

function _build(signals) {
    const byCommand = new Map();
    const byNamespace = new Map();
    const detections = [];
    for (const s of signals) {
        const namespaces = Array.isArray(s.namespaces) ? s.namespaces : [];
        const namespace = namespaces[0] || "";
        const isDetection = s.kind === "detection";
        const overlay = isDetection ? DETECTION_OVERLAY[namespace] : null;
        const command = isDetection ? detectionCommandForNamespace(namespace) : null;
        const def = {
            id: s.id,
            name: s.name,
            kind: s.kind,
            command,
            namespace,
            namespaces,
            label: s.displayName || s.name || command || namespace,
            description: s.description || "",
            tier: overlay?.tier || "alert",
            subject: overlay?.subject || DEFAULT_SUBJECT,
            deployedEnabled: Boolean(s.deployedEnabled),
            deployedVersion: Number(s.deployedVersion) || 0,
            firstParty: Boolean(s.firstParty),
        };
        if (command) byCommand.set(command, def);
        for (const ns of namespaces) if (!byNamespace.has(ns)) byNamespace.set(ns, def);
        if (isDetection) detections.push(def);

        // A second card for any FURTHER namespace the overlay names a command
        // for. Opt-in by construction: a namespace absent from the overlay keeps
        // the collapse above, so adding a facet to any signal still yields one
        // card and this stays a credscan-shaped decision rather than a
        // fleet-wide change of meaning.
        if (!isDetection) continue;
        for (const ns of namespaces.slice(1)) {
            const extra = DETECTION_OVERLAY[ns];
            if (!extra?.command) continue;
            const split = {
                ...def,
                command: extra.command,
                namespace: ns,
                // Scoped to its own namespace so a count for this card cannot
                // sweep in the sibling band it is meant to be compared against.
                namespaces: [ns],
                label: extra.label || def.label,
                tier: extra.tier || def.tier,
                subject: extra.subject || def.subject,
            };
            byCommand.set(extra.command, split);
            // Overrides the collapse above: this namespace resolves to its own
            // card, not the parent signal's.
            byNamespace.set(ns, split);
            detections.push(split);
        }
    }
    // `schemaServed` = the response carried the new schema (any recognized
    // `kind`). It — NOT detection count — is what makes a `ready` cache terminal:
    // a healthy tenant with only extraction signals (or zero detections) still
    // has kinds, so it doesn't poll-loop; a rollout-skew response from an old
    // api-server has no kinds, so it stays non-terminal and re-fetches to heal.
    const schemaServed = signals.some((s) => s.kind === "detection" || s.kind === "extraction");
    _state = { status: "ready", error: null, byCommand, byNamespace, detections, schemaServed };
    _emit();
}

// Fetch the catalog once. Idempotent: concurrent callers share the in-flight
// promise. A `ready` cache whose response carried the new schema (`schemaServed`)
// and a `denied` verdict are terminal (reused, no refetch). But a `ready` from a
// SCHEMA-LESS response is NOT terminal: during a rollout skew an old api-server
// serves signals with no `kind`, so the NEXT hydrate call re-fetches — the listing
// recovers on the next tab mount, navigation, or window refocus (see
// `useSignalCatalog`) once api-server ships the new fields, without a page reload,
// a poll timer, or any throwaway version-skew branch. (A tab left foregrounded +
// idle across the whole window won't refocus, so it waits for one of those events —
// an accepted, minutes-long transient we otherwise handle by deploy order.) Keying
// on `schemaServed` rather than detection COUNT means a healthy tenant with only
// extraction signals (zero detections) is still terminal — it doesn't poll-loop.
// `error` likewise retries (transient); `force` overrides everything.
export function hydrateSignalCatalog({ force = false } = {}) {
    if (_inflight) return _inflight;
    if (!force) {
        if (_state.status === "denied") return Promise.resolve();
        if (_state.status === "ready" && _state.schemaServed) return Promise.resolve();
    }
    _state = { ..._state, status: "loading", error: null };
    _emit();
    const epoch = getAuthIdentityEpoch();
    const request = listSignals()
        .then((signals) => {
            if (getAuthIdentityEpoch() === epoch) _build(signals);
        })
        .catch((err) => {
            if (getAuthIdentityEpoch() !== epoch) return;
            _state = emptyState(err?.status === 403 ? "denied" : "error", err);
            _emit();
        })
        .finally(() => { if (_inflight === request) _inflight = null; });
    _inflight = request;
    return request;
}

export function subscribeSignalCatalog(cb) {
    _listeners.add(cb);
    return () => _listeners.delete(cb);
}

export function signalCatalogStatus() { return _state.status; }

// Reset module state between unit-test cases — the sibling of permissions.js's
// `_resetForTests`, extended here to also drop subscribers.
export function resetSignalCatalogForTests() {
    _state = emptyState();
    _inflight = null;
    _listeners.clear();
}

// Seed the cache directly (bypassing the RPC) so tests exercise the accessors
// without a backend. `signals` is the adaptSignal shape listSignals returns.
export function seedSignalCatalogForTests(signals) {
    _build(Array.isArray(signals) ? signals : []);
}

// ── Sync accessors (read the hydrated cache) ─────────────────────────────────

// The LISTING: detection signals ENABLED for the caller's tenant. Empty until
// hydrated, and per-tenant thereafter (the deploy-state fact, not a bug).
export function alertCommands() {
    // De-dup: two detection namespaces could derive the same command slug; a
    // duplicate id would double-count that signal in the firing-series query.
    return [
        ...new Set(
            _state.detections.filter((d) => d.deployedEnabled && d.command).map((d) => d.command),
        ),
    ].sort();
}
export function alertNamespaces() {
    return _state.detections.filter((d) => d.deployedEnabled && d.namespace).map((d) => d.namespace);
}
// Every detection namespace (enabled AND disabled), excluding extraction. The
// firing-facet query filters on this — not just the enabled set — so a signal
// disabled after it fired stays visible on its historical events until they TTL
// out, rather than vanishing from the dropdown the moment it's turned off.
export function detectionNamespaces() {
    return _state.detections.filter((d) => d.namespace).map((d) => d.namespace);
}
export function namespaceForCommand(cmd) { return _state.byCommand.get(cmd)?.namespace || null; }
export function commandForNamespace(ns) { return _state.byNamespace.get(ns)?.command || null; }
export function signalDefByCommand(cmd) { return _state.byCommand.get(cmd) || null; }
export function labelForCommand(cmd) { return _state.byCommand.get(cmd)?.label || String(cmd ?? ""); }
export function tierForCommand(cmd) { return _state.byCommand.get(cmd)?.tier || "alert"; }

// ── React hook ───────────────────────────────────────────────────────────────

// Subscribe a component to the catalog and ensure it is hydrated. Returns the
// lifecycle status + the (reactive) enabled listing, so a signals surface can
// render loading / permission-denied / error / empty explicitly.
export function useSignalCatalog() {
    const state = useSyncExternalStore(subscribeSignalCatalog, _snapshot, _snapshot);
    useEffect(() => {
        hydrateSignalCatalog();
        // Revalidate when the tab regains visibility. hydrate is a no-op once the
        // catalog is ready-with-content (or denied), so this only re-fetches an
        // empty/errored catalog — healing a rollout-skew empty listing for an
        // analyst who left the Signals tab open and came back, with no poll timer.
        if (typeof document === "undefined") return undefined;
        const onVisible = () => {
            if (document.visibilityState === "visible") hydrateSignalCatalog();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, []);
    return {
        status: state.status,
        ready: state.status === "ready",
        denied: state.status === "denied",
        error: state.status === "error" ? state.error : null,
        alertCommands: state.status === "ready" ? alertCommands() : [],
        // Every detection signal in the tenant's catalog (enabled AND disabled),
        // for the reference panel that shows the whole detection surface — the
        // enabled subset is `alertCommands`. Empty until ready.
        detections: state.status === "ready" ? state.detections : [],
    };
}
